import { useState } from "react";
import { toast } from "sonner";
import { Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { track } from "@/lib/analytics";

const EMPTY = { titolo: "", spiegazione: "", nome: "", website: "" };

export function ProposeTheory({ dossierId }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/dossiers/${dossierId}/proposals`, f);
      setF(EMPTY);
      setOpen(false);
      setDone(true);
      track("theory_proposal", { dossier_id: dossierId });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="propose-section" aria-labelledby="propose-title" className="space-y-4 rounded-[18px] border border-[#DCCDF4] bg-[var(--luma-soft-purple)] p-[22px] sm:p-7">
      <div className="flex items-start gap-4">
        <span aria-hidden="true" className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-[var(--luma-action)] sm:flex"><Lightbulb size={20} /></span>
        <div className="space-y-1.5">
          <p className="luma-eyebrow">Fai la tua proposta</p>
          <h2 id="propose-title" className="text-2xl font-extrabold">Hai una teoria tutta tua?</h2>
          <p className="text-[var(--luma-muted)]">Raccontacela: se aggiunge qualcosa di nuovo, dopo la revisione potrà comparire tra le teorie da esplorare.</p>
        </div>
      </div>
      {done && <p data-testid="proposal-success" className="text-sm font-bold text-emerald-700">Grazie! La tua teoria è in attesa di revisione: comparirà qui se verrà approvata.</p>}
      {!open ? (
        <button type="button" data-testid="propose-theory-button" onClick={() => { setOpen(true); setDone(false); }} className="luma-btn"><Lightbulb size={17} /> Proponi una teoria</button>
      ) : (
        <form data-testid="propose-theory-form" onSubmit={submit} className="space-y-2 rounded-xl bg-white p-4 sm:p-5">
          <label className="luma-label" htmlFor="p-title">Titolo della teoria</label>
          <Input id="p-title" data-testid="proposal-title-input" value={f.titolo} onChange={set("titolo")} required minLength={5} maxLength={140} />
          <label className="luma-label" htmlFor="p-expl">Spiegazione (almeno 30 caratteri)</label>
          <Textarea id="p-expl" data-testid="proposal-explanation-input" value={f.spiegazione} onChange={set("spiegazione")} required minLength={30} maxLength={3000} rows={5} />
          <label className="luma-label" htmlFor="p-name">Nome o nickname (facoltativo, visibile se la teoria viene pubblicata)</label>
          <Input id="p-name" data-testid="proposal-name-input" value={f.nome} onChange={set("nome")} maxLength={30} />
          <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={f.website} onChange={set("website")} aria-hidden="true" />
          <div className="flex gap-2 pt-1">
            <Button data-testid="proposal-submit-button" disabled={busy} className="luma-btn">Invia la proposta</Button>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="proposal-cancel-button" className="min-h-[48px]">Annulla</Button>
          </div>
          <p className="text-xs text-[var(--luma-muted)]">Ogni proposta viene letta dal Fondatore prima della pubblicazione. Non inserire dati personali nel testo.</p>
        </form>
      )}
    </section>
  );
}
